// SHADOW MARKET™ — Kids animated lesson: character + speech bubble + TTS + concept anim.
import React, { useEffect, useState } from "react";
import { useKidsTTS } from "../../hooks/useKidsTTS";
import { Personaje } from "./Personaje";
import { AnimacionConcepto } from "./AnimacionConcepto";
import { getKidsUi } from "../../i18n/kids-academy";

export const LeccionAnimada = ({ personaje, slides, lang = "es", onComplete }) => {
  const [idx, setIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const { hablar, silenciar, hablando } = useKidsTTS(lang);
  const ui = getKidsUi(lang);

  const slide = slides[idx];
  const texto = slide?.texto?.[lang] || slide?.texto?.es || "";
  const isLast = idx === slides.length - 1;

  useEffect(() => {
    if (autoPlay && texto) {
      const t = setTimeout(() => {
        hablar(texto, () => {
          if (!isLast) setTimeout(() => setIdx((i) => i + 1), 900);
        });
      }, 300);
      return () => {
        clearTimeout(t);
        silenciar();
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, autoPlay, lang]);

  const next = () => {
    silenciar();
    if (isLast) {
      onComplete && onComplete();
    } else {
      setIdx((i) => i + 1);
    }
  };
  const prev = () => {
    silenciar();
    if (idx > 0) setIdx((i) => i - 1);
  };
  const toggleAudio = () => {
    setAutoPlay(false);
    if (hablando) silenciar();
    else hablar(texto);
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900/60 to-black/60 p-5 backdrop-blur-xl md:p-7"
      data-testid="kids-leccion-animada"
    >
      {/* Progress dots */}
      <div className="mb-4 flex gap-1.5">
        {slides.map((_, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors"
            style={{ background: i <= idx ? "#f5a623" : "rgba(255,255,255,0.08)" }}
          />
        ))}
      </div>

      <div className="flex flex-col items-center gap-5 md:flex-row md:items-start">
        {/* Character */}
        <div className="shrink-0">
          <Personaje id={personaje} estado={hablando ? "hablar" : "idle"} size={120} />
        </div>

        {/* Speech bubble */}
        <div className="relative flex-1">
          <div className="absolute -left-3 top-5 hidden h-0 w-0 border-y-[8px] border-r-[14px] border-y-transparent border-r-white/[0.08] md:block" />
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-base leading-relaxed text-white/90 md:p-5 md:text-lg">
            {texto}
          </div>
        </div>
      </div>

      {/* Concept animation */}
      {slide.animacion && (
        <div className="mt-5 flex justify-center rounded-xl border border-white/5 bg-black/40 p-4">
          <AnimacionConcepto id={slide.animacion} />
        </div>
      )}

      {/* Controls */}
      <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          onClick={toggleAudio}
          data-testid="kids-audio-toggle"
          className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
            hablando
              ? "border-orange-400/50 bg-orange-400/15 text-orange-200"
              : "border-white/10 bg-white/[0.05] text-white/70 hover:border-orange-400/40"
          }`}
        >
          {hablando ? ui.mute : ui.listen}
        </button>

        <div className="flex gap-2">
          {idx > 0 && (
            <button
              onClick={prev}
              data-testid="kids-prev"
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/65 transition-colors hover:border-white/25 hover:text-white"
            >
              {ui.prev}
            </button>
          )}
          <button
            onClick={next}
            data-testid="kids-next"
            className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_20px_rgba(245,166,35,0.35)] transition-transform hover:-translate-y-0.5"
          >
            {isLast ? ui.completed + " 🎉" : ui.next}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeccionAnimada;
